import {
  AfterViewInit,
  Component,
  ComponentRef,
  createNgModule,
  ElementRef,
  Injector,
  Input,
  NgModuleRef,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { LoadChildrenCallback } from '@angular/router'
import { LazyLoadingService } from 'src/app/services/lazy-loading/lazy-loading.service'
import * as _ from 'lodash-es'
import { logger } from '@utils/logger'
import { ElementLoaderService } from '../services/element-loader.service'
import { ComponentType } from '@angular/cdk/overlay'
import { getComponents } from '@utils/get-components'
import { lazyLoadedComponents } from '@data/lazy-loaded-components'

/**
 * @author Robert Doneux
 * @description Provides lazy loading functionality within X3 for webpack code splitting.
 This replaces the previous method of exporting all angular elements from one module
 leading to large bundle sizes and increasing the initial page load on X3.
 element-loader is now the only exported angular element, and is responsible for
 lazy-loading in the requested module and component
 *
 * @param moduleKey references the lazyLoadedModulesMap @see lazy-loaded-modules.ts
 * @param componentKey references the component reference within the target component
 * @param customProperty[] additional custom properties added to the element-loader component will be
 available within the generated component when declared using @Input()
 *
 * @example <element-loader module-key="hello-world-module" component-key="app-hello-world"></element-loader>
 *
 * @description
 Please note that the target module must reference the target component within a
 components object

 * @example
 export class HelloWorldModule {
 static components(elementLoaderService: ElementLoaderService): Record<string, ComponentType<any>> {
 return getModuleComponents(elementLoaderService, [HelloWorldComponent])
 }
 }
 */

@Component({
  template: '<ng-container #container></ng-container>',
})
export class ElementLoaderComponent implements AfterViewInit {
  @Input() moduleKey!: string
  @Input() componentKey!: string

  @ViewChild('container', { read: ViewContainerRef })
  private viewRef!: ViewContainerRef

  constructor(
    private injector: Injector,
    private elementRef: ElementRef,
    private lazyLoadingService: LazyLoadingService,
    private elementLoaderService: ElementLoaderService,
  ) {}

  ngAfterViewInit() {
    if (this.moduleKey && this.componentKey) {
      this.loadModuleComponent().then()
    } else if (this.componentKey) {
      this.loadStandaloneComponent()
    } else {
      throw Error('Cannot lazy load component without component-key definition')
    }
  }

  private async loadModuleComponent() {
    logger('loader', `module component: ${this.moduleKey} -> ${this.componentKey}`)
    const callback: LoadChildrenCallback = this.lazyLoadingService.getLoadChildrenCallback(this.moduleKey)
    const module: any = await callback()
    const components: Record<string, ComponentType<any>> = module.components(this.elementLoaderService)
    const component: ComponentType<any> = components[this.componentKey]
    this.openLazyLoadedComponent(component, module)
  }

  private loadStandaloneComponent() {
    logger('loader', `standalone component: ${this.componentKey}`)
    const components: Record<string, ComponentType<any>> = getComponents(this.elementLoaderService, lazyLoadedComponents)
    const component: ComponentType<any> = components[this.componentKey]
    this.openLazyLoadedComponent(component)
  }

  private openLazyLoadedComponent(component: ComponentType<any>, module: Type<any> = null): void {

    const moduleRef: NgModuleRef<any> = module ? createNgModule(module, this.injector) : null

    const componentRef: ComponentRef<any> = this.viewRef.createComponent(component, {
      index: 0,
      injector: this.injector,
      projectableNodes: [],
      environmentInjector: moduleRef,
    })

    this.passInputDataToGeneratedComponent(componentRef)
  }

  private passInputDataToGeneratedComponent(componentRef: ComponentRef<any>): void {
    const elementRef: HTMLElement = this.elementRef.nativeElement as HTMLElement
    elementRef.getAttributeNames().forEach((element: string) => {
      componentRef.instance[_.camelCase(element)] = elementRef.getAttribute(element)
    })
  }
}
